<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Text } from '@Shelly/shelly-design-system';
	import { openExternalLink } from 'src/helpers/utilities';

	let isVisible = false;
	let containerRef: HTMLElement;

	// Get today's date in German format (DD.MM.YYYY)
	const getTodayDate = (): string => {
		const today = new Date();
		const day = String(today.getDate()).padStart(2, '0');
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const year = today.getFullYear();
		return `${day}.${month}.${year}`;
	};

	$: todayDate = getTodayDate();

	onMount(() => {
		if (!containerRef) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						isVisible = true;
						observer.disconnect();
					}
				});
			},
			{
				threshold: 0.1,
				rootMargin: '50px',
			},
		);

		observer.observe(containerRef);

		return () => {
			observer.disconnect();
		};
	});

	const handleGuidelinesClick = () => {
		openExternalLink(
			'https://www.verivox.de/company/tarifrechnergrundlage-energie/?tunnel-campaign=2&tunnel-sub-partner=5762',
		);
	};

	const handleProvidersClick = () => {
		openExternalLink(
			'https://www.verivox.de/strom/anbieter/?fl=all&tunnel-campaign=2&tunnel-sub-partner=5762',
		);
	};

	const handleEmailClick = () => {
		window.location.href = 'mailto:service@verivox.de';
	};
</script>

<div bind:this={containerRef} class="consumer-protection-wrapper" class:visible={isVisible}>
	<div class="consumer-protection-content">
		<Text
			element="h2"
			class="protection-title text-primary cursor-pointer"
			on:click={handleGuidelinesClick}
		>
			{$format('verivox_consumer_protection_title', {
				default: 'Verivox-Richtlinien zum Verbraucherschutz',
				locale: 'de',
			})}
		</Text>
		<Text styleType="body-small" class="protection-date">
			{$format('verivox_consumer_protection_date_prefix', { default: 'Stand:', locale: 'de' })}
			{todayDate}
		</Text>

		<Text styleType="h2" on:click={handleProvidersClick} class="text-primary cursor-pointer">
			{$format('verivox_consumer_protection_section_heading', {
				default: 'Berücksichtigte Stromanbieter',
				locale: 'de',
			})}
		</Text>

		<Text styleType="body-small" class="protection-text">
			{$format('verivox_consumer_protection_paragraph_1', {
				default:
					'Der Tarifrechner von Verivox zeigt über 90 Prozent der verfügbaren Online-Angebote an, darunter zahlreiche und günstige überregionale Angebote sowie kleinere regionale Anbieter. Einige Unternehmen möchten ausdrücklich nicht in unseren Rechnern dargestellt werden, was wir bedauern, da wir so den Markt nicht vollständig abbilden können – weder für uns noch für die Verbraucher.',
				locale: 'de',
			})}
		</Text>

		<Text styleType="body-small" class="protection-text">
			{$format('verivox_consumer_protection_paragraph_2', {
				default:
					'Die Ergebnisse berücksichtigen Anbieter und Tarife gemäß Ihrer Einstellungen und den Richtlinien des Verivox-Preisrechners für Strom, Gas und Heizstrom. Alle angegebenen Preise sind Bruttopreise inkl. MwSt. sowie aller weiteren Steuern, Abgaben und Umlagen. Die Preise beziehen sich auf das erste Jahr, die angegebene Postleitzahl sowie den angegebenen Verbrauch.',
				locale: 'de',
			})}
		</Text>

		<Text styleType="body-small" class="protection-text">
			{$format('verivox_consumer_protection_copyright', {
				default:
					'Alle Daten © 1999-2024 bei Verivox GmbH, Heidelberg. Das Angebot darf nur für den privaten Gebrauch genutzt werden. Eine Vervielfältigung oder Verbreitung des Angebots oder der auf diesen Seiten angezeigten Informationen – ganz oder auszugsweise, gleich in welcher Form – und jede Form der kommerziellen Verwendung ist nicht ohne ausdrückliche schriftliche Zustimmung der Verivox GmbH zulässig.',
				locale: 'de',
			})}
			{' '}
			{$format('verivox_consumer_protection_email_label', {
				default:
					'Wenn Sie die Informationen für kommerzielle Zwecke nutzen möchten, kontaktieren Sie uns bitte per E-Mail:',
				locale: 'de',
			})}
			{' '}
			<a
				href="mailto:service@verivox.de"
				class="email-link"
				on:click|preventDefault={handleEmailClick}
			>
				service@verivox.de
			</a>
		</Text>
	</div>
</div>

<style>
	.consumer-protection-wrapper {
		opacity: 0;
		transform: translateY(20px);
		transition:
			opacity 0.5s ease-in-out,
			transform 0.5s ease-in-out;
		margin-top: var(--spacing-l);
		padding-top: var(--spacing-l);
		border-top: 1px solid var(--color-border, #e5e7eb);
	}

	.consumer-protection-wrapper.visible {
		opacity: 1;
		transform: translateY(0);
	}

	.consumer-protection-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
		padding: var(--spacing-m);
		border-radius: 8px;
	}

	.protection-title {
		margin: 0;
		font-weight: 700;
		color: var(--color-text-primary, #000000);
		font-size: 18px;
		line-height: 1.4;
	}

	.protection-date {
		margin: 0;
		color: var(--color-text-secondary, #6b7280);
		font-size: 12px;
		font-style: italic;
	}

	.protection-text {
		margin: 0;
		color: var(--color-text-secondary, #6b7280);
		font-size: 13px;
		line-height: 1.6;
	}

	.email-link {
		color: var(--color-primary, #0066cc);
		text-decoration: none;
		transition: color 0.2s ease;
	}

	.email-link:hover {
		color: var(--color-primary-dark, #0052a3);
		text-decoration: underline;
	}

	@media (max-width: 768px) {
		.consumer-protection-content {
			padding: var(--spacing-s);
		}

		.protection-title {
			font-size: 16px;
		}

		.protection-text {
			font-size: 12px;
		}
	}
</style>
